import { type CategoryId, type CalendarEvent, type TimePeriod } from "./calendarTypes";

export const defaultCategories: { id: CategoryId; label: string }[] = [
  { id: "work", label: "Work" },
  { id: "personal", label: "Personal" },
];

export function isoToLocalDate(iso: string | null | undefined): Date {
  if (!iso) return new Date();
  const parts = iso.split("-").map((p) => parseInt(p, 10));
  const [year, month, day] = [parts[0], parts[1] || 1, parts[2] || 1];
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

export function mergeCategories(base: { id: string; label: string }[], extras: { id: string; label: string }[]) {
  const seen = new Set<string>();
  const out: { id: string; label: string }[] = [];
  for (const c of [...base, ...extras]) {
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    out.push(c);
  }
  return out;
}

export function buildTimeString(hour?: number, minute?: number, period?: TimePeriod) {
  if (hour == null || minute == null || !period) return undefined;
  const hour24 = ((hour % 12) + (period === "PM" ? 12 : 0)) % 24;
  const hh = hour24.toString().padStart(2, "0");
  const mm = minute.toString().padStart(2, "0");
  return `${hh}:${mm}`;
}

export function parseTimeString(time?: string | null): { timeHour: number; timeMinute: number; timePeriod: TimePeriod } | null {
  if (!time) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!match) return null;
  const hour24 = Number(match[1]);
  const minute = Number(match[2]);
  const period: TimePeriod = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { timeHour: hour12, timeMinute: minute, timePeriod: period };
}

export function mergeEventLists(current: CalendarEvent[], incoming: CalendarEvent[], deleted: Set<string>): CalendarEvent[] {
  const map = new Map<string, CalendarEvent>();
  for (const ev of incoming) {
    if (!deleted.has(ev.id)) map.set(ev.id, ev);
  }
  for (const ev of current) {
    if (!deleted.has(ev.id) && !map.has(ev.id)) {
      map.set(ev.id, ev);
    }
  }
  return Array.from(map.values());
}

export function isGoogleEvent(evt: CalendarEvent): boolean {
  return typeof evt.id === "string" && evt.id.startsWith("gcal:");
}
